import flags from "@assets/images/flags.png";
import { IContactNumber } from "@common/models";
import { IViewport } from "@config/appConfig";
import { IAppState } from "@misc/State";
import Config from "@service/Config";
import Translate from "@service/Translate";
import { DigitsToEN, nameof } from "@utilize/Util";
import { FC } from "react";
import { useSelector } from "react-redux";
import Icon, { IconMode } from "../Icon";
import DropDown, { DisplayMode } from "./DropDown";
import { IFormControlProps } from "./Form";
import TextInput from "./TextInput";

interface IPhoneSectionProps extends IFormControlProps {
    type?: "phone" | "tel";
    index?: number;
    readonlyInternationalCode?: boolean;
    readonlyAreaCode?: boolean;
    readonlyLineNumber?: boolean;
    dataSource?: any;
    dataMember?: any;
    freezeCallingCode?: string;
    size?: "default" | "small";
    error?: any;
}

const PhoneSection: FC<IPhoneSectionProps> = ({
    className,
    index,
    label,
    required,
    type,
    error,
    disabled,
    readonlyInternationalCode,
    readonlyAreaCode,
    readonlyLineNumber,
    readonly,
    dataSource = [],
    dataMember,
    freezeCallingCode,
    size,
    autoComplete = "new-password",
    onBlur,
    onChange
}) => {
    const { tr } = Translate;
    const { countriesData, width } = useSelector((state: IAppState) => ({
        countriesData: state.countriesData,
        width: state.windowWidth
    }));

    if (freezeCallingCode && !dataSource[dataMember]?.internationalCode)
        dataSource[dataMember] = { internationalCode: countriesData.find(c => c.callingCode === freezeCallingCode.toString())?.callingCode };

    const onTextInputBlur = (name: string, value: string) => {
        value = DigitsToEN(value);
        value = value.replace(/^0+/, '').replace(/^۰+/, '');
        if (onBlur) {
            onBlur(name, value);
        }
    }

    const onChangePhoneNumber = (name: string, value: string) => {
        value = DigitsToEN(value);
        value = value.replace(/^0+/, '').replace(/^۰+/, '');
        if (onChange) {
            onChange(name, value);
        }
    }

    return (
        <div className={`form-group phone-section has-value form-focused${className ? ` ${className}` : ""}${error?.areaCode || error?.internationalCode || error?.phoneNumber ? " has-error" : ""}`}>
            {label ? <label htmlFor={window.name}>{label}{required ? <Icon name="icon-required" className="filed-required" size="8px" /> : null}</label> : null}
            <div className="form-container">
                <div className={`${type}-section-items`}>
                    <DropDown
                        name={nameof<IContactNumber>("internationalCode")}
                        id={`${nameof<IContactNumber>("internationalCode")}${index ?? ''}`}
                        className="international-code"
                        autoComplete="off"
                        value={countriesData.find(c => c.callingCode === (dataSource[dataMember]?.internationalCode))}
                        placeholder={tr("id")}
                        valueKey="callingCode"
                        onChange={onChange}
                        itemDisplayFormat={`{name} - {callingCode}`}
                        dataSource={countriesData}
                        disabled={disabled}
                        size={size}
                        readonly={readonlyInternationalCode || readonly}
                        dir={width > Config.get<IViewport>("viewport").small ? "ltr" : "rtl"}
                        nextElement={`${nameof<IContactNumber>(type === "tel" ? "areaCode" : "phoneNumber")}${index ?? ''}`}
                        displayMode={DisplayMode.TextAndIcon}
                        displayFormat="callingCode"
                        iconSetting={{ mode: IconMode.Position, iconField: "flagPosition", path: flags, width: "24px", height: "3876px" }}
                    />
                    {
                        type === "tel" ?
                            <TextInput
                                name={nameof<IContactNumber>("areaCode")}
                                type="tel"
                                id={`${nameof<IContactNumber>("areaCode")}${index ?? ''}`}
                                size={size}
                                autoComplete={autoComplete}
                                dataSource={dataSource[dataMember]}
                                dataMember={nameof<IContactNumber>("areaCode")}
                                onChange={onChange}
                                placeholder={tr("phone_area_code")}
                                disabled={disabled || !dataSource[dataMember]?.internationalCode}
                                onBlur={onTextInputBlur}
                                readonly={readonlyAreaCode || readonly}
                            />
                            : ""
                    }
                    <div className={`phone-splitter${disabled ? " splitter-disable" : ""}`} />
                    <TextInput
                        name={nameof<IContactNumber>("phoneNumber")}
                        id={`${nameof<IContactNumber>("phoneNumber")}${index ?? ''}`}
                        type="tel"
                        autoComplete={autoComplete}
                        dataSource={dataSource[dataMember]}
                        size={size}
                        dataMember={nameof<IContactNumber>("phoneNumber")}
                        onChange={onChangePhoneNumber}
                        placeholder={tr("number_without_zero")}
                        disabled={disabled || !dataSource[dataMember]?.internationalCode}
                        readonly={readonlyLineNumber || readonly}
                        onBlur={onTextInputBlur}
                    />
                </div>
                <fieldset aria-hidden="true" className="form-fieldset">
                    <legend className="form-fieldset-legend">
                        {label ? <label htmlFor={window.name}>{label}{required ? <Icon name="icon-required" className="filed-required" size="8px" /> : null}</label> : null}
                    </legend>
                </fieldset>
            </div>
            <p className="form-error">{error?.areaCode || error?.internationalCode || error?.phoneNumber}</p>
        </div>
    )

}

export default PhoneSection;